import { createIsomorphicFn, createServerFn } from '@tanstack/react-start';
import { Client as StytchClient, StytchError } from 'stytch';

import { useAppSession } from '@/auth/session';

const STYTCH_SESSION_DURATION_MINUTES = 43200;

let stytch: StytchClient | null = null;

export const getStytch = createIsomorphicFn()
  .server(() => {
    if (stytch != null) {
      return stytch;
    }
    stytch = new StytchClient({
      project_id: process.env.STYTCH_PROJECT_ID ?? '',
      secret: process.env.STYTCH_SECRET ?? '',
      // Must match the API's STYTCH_APP_PROJECT_DOMAIN: Stytch derives session
      // JWT issuers from the domain a request arrives on.
      custom_base_url: process.env.STYTCH_PROJECT_DOMAIN || undefined,
    });
    return stytch;
  })
  .client(() => {
    throw new Error('getStytch should not be called on the client');
  });

export const getTokenFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const client = getStytch();
    const session = await useAppSession();

    if (session.data?.stytchJwt == null) {
      return null;
    }

    try {
      const authenticationRequest = {
        session_jwt: session.data.stytchJwt,
        ...(session.data.impersonation == null
          ? { session_duration_minutes: STYTCH_SESSION_DURATION_MINUTES }
          : {}),
      };
      const response = await client.sessions.authenticate(
        authenticationRequest,
      );

      return {
        token: response.session_jwt,
        expiresAt: new Date(Date.now() + 5 * 60 * 1000),
      };
    } catch (error) {
      if (
        error instanceof StytchError &&
        error.status_code === 404 &&
        error.error_type === 'session_not_found'
      ) {
        await session.clear();
        return null;
      }

      throw error;
    }
  },
);

export const isAuthenticatedFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const session = await useAppSession();
    return session.data?.stytchJwt != null;
  },
);

export { STYTCH_SESSION_DURATION_MINUTES };
